import { NetworkType } from 'abis/set/sets'
import { atom } from 'jotai'
import { createAtomsWithQuery } from 'services/query-v2/atomic-factory'
import { QUERY_KEY_PRODUCT_INFO } from 'services/query-v2/keys'
import { DEFAULT_REFETCH_INTERVAL } from 'services/query-v2/refetch-intervals'
import { fetchGraphQLData } from 'services/query/fetchers/data-graph'
import { getSubgraphNewUrl } from 'services/query/hooks'
import { EmptyArrayError } from 'utils/errors'
import { Address, zeroAddress } from 'viem'
import { Product, RawIndexProductResponse, parseProduct } from './model'
import { getParamsForIndexInfoData } from './query-params'
import { z } from 'zod'

const ResponseSchema = z.object({
  indexes: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      symbol: z.string(),
      totalSupply: z.string(),
      aumFee: z.string(),
      mintingFee: z.string(),
      redemptionFee: z.string(),
      totalFees: z.string().nullable(),
      creationDate: z.string(),
      k: z.string().nullable(),
      version: z.string(),
      assets: z.array(
        z.object({
          assets: z.array(
            z.object({
              chainID: z.string(),
              asset: z.string(),
              balance: z.string(),
              currencyID: z.string().nullable(),
              decimals: z.number(),
              weight: z.string().nullable(),
              name: z.string(),
              symbol: z.string(),
            }),
          ),
          chainID: z.string(),
          chainIndex: z.string().nullable(),
          latestSnapshot: z.string().nullable(),
        }),
      ),
    }),
  ),
})

export const getIndexInfoParams = (address: string) => ({
  url: getSubgraphNewUrl(address),
  params: getParamsForIndexInfoData(address),
})
export const indexQuery = async <T>({
  queryKey: [key, address, network],
}: {
  queryKey: [string, Address, NetworkType]
}) => {
  const { url, params } = getIndexInfoParams(address)

  const res = await fetchGraphQLData<RawIndexProductResponse>(url, params)
  if (!res) {
    throw new Error(`Fails in indexQuery function with a key ${key}`)
  }

  const response = ResponseSchema.parse(res)
  if (response.indexes.length === 0) throw new EmptyArrayError()
  return parseProduct(response.indexes[0], network, key) as T
}

export const indexDataFactory = () => {
  const indexAddressAtom = atom<Address>(zeroAddress)
  const indexNetworkAtom = atom<NetworkType>(NetworkType.Mainnet)

  const mainProperties = createAtomsWithQuery<
    Product,
    Address,
    NetworkType,
    null,
    [string, Address, NetworkType]
  >(
    { first: indexAddressAtom, second: indexNetworkAtom },
    QUERY_KEY_PRODUCT_INFO,
    indexQuery<Product>,
    DEFAULT_REFETCH_INTERVAL,
    'indexQuery',
  )

  return { chainId: indexNetworkAtom, productAddress: indexAddressAtom, ...mainProperties }
}
